import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import useDebounce from "shared/hooks/useDebounce";
import SekeletonLoader from "shared/loaders/skeleton";
import { routeConstant } from "shared/routes/routeConstant";
import { GetPendingEvaluationsService } from "shared/services/evaluations";
import { JobRole } from "shared/utils/enum";
import styles from "./styles.module.scss";
import { GetCountService } from "shared/services/general";
import { setCountReducer } from "shared/redux/reducers/count";
import { useDispatch } from "react-redux";

const Heads: CustomTableHeadType[] = [
  {
    title: "Job Title",
    key: "jobTitle",
  },
  {
    title: "Department",
    key: "department",
  },
  {
    title: "Job Code",
    key: "jobCode",
  },
  {
    title: "Role Type",
    key: "roleType",
  },
  {
    title: "Requested by",
    key: "requestedBy",
  },
  {
    title: "Organization",
    key: "organization",
  },
  {
    title: "Date",
    key: "evaluationDate",
  },
  {
    title: "Action",
    key: "",
  },
];

const PendingEvaluations = () => {
  const dispatch = useDispatch();

  const pageRef = useRef<number>(1);

  const [loading, setLoading] = useState<boolean>(true);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [searchVal, setSearchVal] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [order, setOrder] = useState<string>("");

  const handleGetEvaluations = () => {
    GetPendingEvaluationsService({
      search: searchVal,
      pageNo: pageRef.current,
      sortBy,
      order,
    })
      .then(({ data: { data, total } }) => {
        setEvaluations(data);
        setTotal(total);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const handleGetCount = () => {
    GetCountService()
      .then(({ data }) => {
        dispatch(
          setCountReducer({
            complaints: data?.complaints_count,
            orgs: data?.orgs_count,
            req: data?.req_count,
          })
        );
      })
      .catch((err) => {});
  };

  useEffect(() => {
    setPage(1);
    pageRef.current = 1;
    setEvaluations([]);
    setLoading(true);
    handleGetEvaluations();
    // eslint-disable-next-line
  }, [searchVal, sortBy, order]);

  useEffect(() => {
    handleGetCount();
    // eslint-disable-next-line
  }, []);

  useDebounce(
    () => {
      setSearchVal(search);
    },
    [search],
    800
  );

  return (
    <div className={classNames(styles.mainContainer)}>
      <div
        className={classNames(
          "flex items-start gap-3 lg:items-center justify-between flex-col lg:flex-row px-4 py-4",
          styles.tableMainHead
        )}
      >
        <div className={classNames("flex flex-col items-start")}>
          <h1 className={classNames(styles.tableMainHeading)}>
            Requests Pending Evaluation by CGP
          </h1>
        </div>
        <div className={classNames("flex flex-wrap items-center gap-3")}>
          <div className={classNames(styles.searchContainer, "flex")}>
            <Icons.Search className={classNames(styles.searchIconStyle)} />
            <input
              className={classNames(styles.searchInputStyle, "ps-2")}
              value={search}
              placeholder="Search Job Title / Dept"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>
      </div>
      <CustomTable
        title="Requested Evaluation History"
        heads={Heads}
        loading={loading}
        isEmpty={evaluations.length === 0}
        stickSecondCol={false}
        sortBy={sortBy}
        setSortBy={setSortBy}
        order={order}
        setOrder={setOrder}
      >
        {loading ? (
          <>
            {Array.from(Array(5).keys()).map((_, index) => {
              return <TableRowLoader key={index} />;
            })}
          </>
        ) : (
          <>
            {evaluations.map((evaluation, index) => {
              return <TableRow item={evaluation} key={index} index={index} />;
            })}
          </>
        )}
      </CustomTable>
      <div
        className={classNames(
          "w-full px-4 py-4",
          total >= 10 && styles.border,
          styles.paginationContainer
        )}
      >
        <CustomPagination
          currentPage={page}
          totalCount={total}
          pageSize={10}
          onPageChange={(page: any) => {
            setPage(page);
            pageRef.current = page;
            setLoading(true);
            handleGetEvaluations();
          }}
        />
      </div>
    </div>
  );
};

const TableRow = ({ item, index }: any) => {
  const navigate = useNavigate();
  return (
    <>
      <tr>
        <td
          className={classNames(
            styles.tableContentLabel,
            "ps-4 pe-2 py-3 sticky-col first-col"
          )}
        >
          {item?.current_version?.jobTitle}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ maxWidth: "200px" }}
        >
          {item?.current_version?.department}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ maxWidth: "100px" }}
        >
          {item?.jobCode}
        </td>

        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.roleType === JobRole.management
            ? "Manager"
            : "Individual Contributor"}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.requested_by?.name}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.organization_id?.name}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {moment(item?.current_version?.created_at).format("MMM DD, YYYY")}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{ verticalAlign: "middle", textAlign: "center" }}
        >
          <div className={classNames("flex items-center gap-2 justify-center")}>
            <CustomButton
              title="Continue"
              Icon={Icons.Pencil}
              IconDirction="right"
              containerStyle={classNames(styles.actionButton)}
              iconStyle={classNames(styles.actionIcon)}
              onClick={() => {
                navigate(
                  routeConstant.continuePendingEvaluation.path.replace(
                    ":id",
                    item.id
                  )
                );
              }}
            />
          </div>
        </td>
      </tr>
    </>
  );
};
const TableRowLoader = () => {
  return (
    <>
      <tr>
        <td className={classNames(styles.tableContentLabel, "ps-4 pe-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center gap-2")}>
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
          </div>
        </td>
      </tr>
    </>
  );
};

export default PendingEvaluations;
